import fetchWithAuth from "@/api/secureFetch";
import {
    ContextMeshApiError,
    parseRecommendation,
    parseSourceDiagnostic,
    type ContextMeshCategoryKey,
    type ContextMeshRecommendation,
    type ContextMeshSourceDiagnostic
} from "./contextMeshAnalysis";

export type ContextMeshReportItemStatus = "rated" | "not_rated" | "processing" | "failed";
export type ContextMeshRecommendationStatus = "not_requested" | "analyzing" | "ready" | "failed";
export type ContextMeshReportRelevance = "relevant" | "low_relevance" | "not_rated";
export type ContextMeshReportContextRole = "existing_product" | "requested_feature" | "standard_capability" | "both" | "none";
export type ContextMeshReportEvidenceRole = "direct_evidence" | "supporting_product_context" | "not_direct_evidence" | "duplicate" | "not_analyzed";
export type ContextMeshReportScoreType = "direct_evidence" | "support_relevance";
export type ContextMeshSupportingAnchorType = "existing_product" | "standard_capability";
export type ContextMeshSupportingBasis = "purpose_context" | "existing_capability" | "explicit_standard" | "inferred_standard";
export type ContextMeshSupportingMatchedBy = "content" | "content_and_file_context" | "content_and_ticket_context";
export type ContextMeshReportDetailStatus = "sufficient" | "needs_detail" | "not_applicable" | "not_analyzed";

export const SUPPORTING_CONTEXT_CLASSIFICATION_VERSION = "supporting-product-context-v4";
export const HISTORICAL_SUPPORTING_CONTEXT_CLASSIFICATION_VERSIONS = new Set([
    "supporting-product-context-v1",
    "supporting-product-context-v2",
    "supporting-product-context-v3"
]);

export interface ContextMeshSupportingContext {
    anchorType: ContextMeshSupportingAnchorType;
    anchorName: string;
    basis?: ContextMeshSupportingBasis;
    matchedBy?: ContextMeshSupportingMatchedBy;
    inferenceReason?: string;
}

export interface ContextMeshClassificationCounts {
    directEvidence?: number;
    needsDetail?: number;
    supportingProductContext?: number;
    notDirectEvidence?: number;
    duplicate?: number;
    notAnalyzed?: number;
}

export interface ContextMeshAnchorCoverage {
    covered: number;
    required: number;
}

export interface ContextMeshCoverageSummary {
    existingProduct: ContextMeshAnchorCoverage;
    requestedFeature: ContextMeshAnchorCoverage;
}

export interface ContextMeshEvidenceCounts {
    analyzed: number;
    qualified: number;
    selected: number;
    requiredCapabilities: number;
    coveredCapabilities: number;
}

export type ContextMeshDiscoverFindingStatus = "present" | "partial" | "missing";
export type ContextMeshDiscoverAlignmentStatus = "ready" | "unavailable" | "not_applicable";

export interface ContextMeshDiscoverFindingEvidence {
    field: string;
    quote: string;
}

export interface ContextMeshDiscoverFinding {
    code: string;
    criterion: string;
    status: ContextMeshDiscoverFindingStatus;
    evidence: ContextMeshDiscoverFindingEvidence[];
    suggestion: string;
}

export interface ContextMeshDiscoverQuality {
    score: number;
    feedback: string;
    findings: ContextMeshDiscoverFinding[];
}

export interface ContextMeshDiscoverAlignment {
    status: ContextMeshDiscoverAlignmentStatus;
    score: number | null;
    feedback: string;
    findings: ContextMeshDiscoverFinding[];
}

export interface ContextMeshReportItem {
    id: string;
    type: string;
    parentId: string | null;
    name: string;
    path: string | null;
    score: number | null;
    status: ContextMeshReportItemStatus;
    relevance: ContextMeshReportRelevance;
    relevanceReason: string;
    currentState: string;
    method: string;
    recommendation: ContextMeshRecommendation | null;
    recommendationStatus?: ContextMeshRecommendationStatus;
    hasChildren: boolean;
    childCount: number;
    evidenceCounts?: ContextMeshEvidenceCounts;
    contextRole?: ContextMeshReportContextRole;
    coverageSummary?: ContextMeshCoverageSummary;
    evidenceRole?: ContextMeshReportEvidenceRole;
    scoreType?: ContextMeshReportScoreType | null;
    supportingContext?: ContextMeshSupportingContext;
    detailStatus?: ContextMeshReportDetailStatus;
    contributesToScore?: boolean;
    classificationCounts?: ContextMeshClassificationCounts;
    quality?: ContextMeshDiscoverQuality;
    alignment?: ContextMeshDiscoverAlignment;
}

export interface ContextMeshReportPage {
    productId: number | null;
    contextMeshId?: string | null;
    runId: string;
    scoringVersion: string;
    formulaVersion: string;
    category: ContextMeshCategoryKey;
    categoryScore: number;
    method: string;
    classificationVersion?: string;
    sourceDiagnostic?: ContextMeshSourceDiagnostic | null;
    items: ContextMeshReportItem[];
    nextCursor: string | null;
}

export type ContextMeshReportTarget =
    | { productId: number; contextMeshId?: never }
    | { contextMeshId: string; productId?: never };

export interface ContextMeshReportQuery {
    category: ContextMeshCategoryKey;
    parentType?: string;
    parentId?: string;
    cursor?: string;
    limit?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requiredString(value: unknown, field: string): string {
    if (typeof value !== "string" || !value.trim()) throw new Error(`Invalid Context Mesh report: ${field}`);
    return value;
}

function requiredNumber(value: unknown, field: string): number {
    if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    return value;
}

function requiredText(value: unknown, field: string): string {
    if (typeof value !== "string") throw new Error(`Invalid Context Mesh report: ${field}`);
    return value;
}

function scoreNumber(value: unknown, field: string): number {
    const score = requiredNumber(value, field);
    if (!Number.isInteger(score) || score < 0 || score > 100) {
        throw new Error(`Invalid Context Mesh report: ${field}`);
    }
    return score;
}

function parseDiscoverFinding(value: unknown, field: string): ContextMeshDiscoverFinding {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    const status = requiredString(value.status, `${field}.status`) as ContextMeshDiscoverFindingStatus;
    if (!(["present", "partial", "missing"] as const).includes(status)) {
        throw new Error(`Invalid Context Mesh report: ${field}.status`);
    }
    if (!Array.isArray(value.evidence)) throw new Error(`Invalid Context Mesh report: ${field}.evidence`);
    return {
        code: requiredString(value.code, `${field}.code`),
        criterion: requiredString(value.criterion, `${field}.criterion`),
        status,
        evidence: value.evidence.map((evidence, evidenceIndex) => {
            const evidenceField = `${field}.evidence[${evidenceIndex}]`;
            if (!isRecord(evidence)) throw new Error(`Invalid Context Mesh report: ${evidenceField}`);
            return {
                field: requiredString(evidence.field, `${evidenceField}.field`),
                quote: requiredString(evidence.quote, `${evidenceField}.quote`)
            };
        }),
        suggestion: requiredText(value.suggestion, `${field}.suggestion`)
    };
}

function parseDiscoverFindings(value: unknown, field: string): ContextMeshDiscoverFinding[] {
    if (!Array.isArray(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    return value.map((finding, index) => parseDiscoverFinding(finding, `${field}[${index}]`));
}

function parseDiscoverQuality(value: unknown, field: string): ContextMeshDiscoverQuality {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    return {
        score: scoreNumber(value.score, `${field}.score`),
        feedback: requiredText(value.feedback, `${field}.feedback`),
        findings: parseDiscoverFindings(value.findings, `${field}.findings`)
    };
}

function parseDiscoverAlignment(value: unknown, field: string): ContextMeshDiscoverAlignment {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    const status = requiredString(value.status, `${field}.status`) as ContextMeshDiscoverAlignmentStatus;
    if (!(["ready", "unavailable", "not_applicable"] as const).includes(status)) {
        throw new Error(`Invalid Context Mesh report: ${field}.status`);
    }
    const score = value.score === null ? null : scoreNumber(value.score, `${field}.score`);
    if ((status === "ready") !== (score !== null)) {
        throw new Error(`Invalid Context Mesh report: ${field}.score`);
    }
    return {
        status,
        score,
        feedback: requiredText(value.feedback, `${field}.feedback`),
        findings: parseDiscoverFindings(value.findings, `${field}.findings`)
    };
}

function parseEvidenceCounts(value: unknown, field: string): ContextMeshEvidenceCounts {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    const counts: ContextMeshEvidenceCounts = {
        analyzed: requiredNumber(value.analyzed, `${field}.analyzed`),
        qualified: requiredNumber(value.qualified, `${field}.qualified`),
        selected: requiredNumber(value.selected, `${field}.selected`),
        requiredCapabilities: requiredNumber(value.requiredCapabilities, `${field}.requiredCapabilities`),
        coveredCapabilities: requiredNumber(value.coveredCapabilities, `${field}.coveredCapabilities`)
    };
    if (Object.values(counts).some(count => !Number.isInteger(count) || count < 0)) {
        throw new Error(`Invalid Context Mesh report: ${field}`);
    }
    if (
        counts.qualified > counts.analyzed
        || counts.selected > counts.qualified
        || counts.coveredCapabilities > counts.requiredCapabilities
    ) {
        throw new Error(`Invalid Context Mesh report: ${field}`);
    }
    return counts;
}

function parseAnchorCoverage(value: unknown, field: string): ContextMeshAnchorCoverage {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    const coverage = {
        covered: requiredNumber(value.covered, `${field}.covered`),
        required: requiredNumber(value.required, `${field}.required`)
    };
    if (
        !Number.isInteger(coverage.covered)
        || coverage.covered < 0
        || !Number.isInteger(coverage.required)
        || coverage.required < 0
        || coverage.covered > coverage.required
    ) {
        throw new Error(`Invalid Context Mesh report: ${field}`);
    }
    return coverage;
}

function parseCoverageSummary(value: unknown, field: string): ContextMeshCoverageSummary {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    return {
        existingProduct: parseAnchorCoverage(value.existingProduct, `${field}.existingProduct`),
        requestedFeature: parseAnchorCoverage(value.requestedFeature, `${field}.requestedFeature`)
    };
}

const CLASSIFICATION_COUNT_KEYS = [
    "directEvidence",
    "needsDetail",
    "supportingProductContext",
    "notDirectEvidence",
    "duplicate",
    "notAnalyzed"
] as const satisfies readonly (keyof ContextMeshClassificationCounts)[];

function parseClassificationCounts(value: unknown, field: string): ContextMeshClassificationCounts {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    const allowedKeys = new Set<string>(CLASSIFICATION_COUNT_KEYS);
    if (Object.keys(value).some(key => !allowedKeys.has(key))) {
        throw new Error(`Invalid Context Mesh report: ${field}`);
    }
    const counts: ContextMeshClassificationCounts = {};
    for (const key of CLASSIFICATION_COUNT_KEYS) {
        if (value[key] == null) continue;
        const count = requiredNumber(value[key], `${field}.${key}`);
        if (!Number.isInteger(count) || count < 0) {
            throw new Error(`Invalid Context Mesh report: ${field}.${key}`);
        }
        counts[key] = count;
    }
    return counts;
}

function parseSupportingContext(value: unknown, field: string): ContextMeshSupportingContext {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    const anchorType = requiredString(value.anchorType, `${field}.anchorType`) as ContextMeshSupportingAnchorType;
    if (!(["existing_product", "standard_capability"] as const).includes(anchorType)) {
        throw new Error(`Invalid Context Mesh report: ${field}.anchorType`);
    }
    const supportingContext: ContextMeshSupportingContext = {
        anchorType,
        anchorName: requiredString(value.anchorName, `${field}.anchorName`)
    };
    if (value.basis != null) {
        const basis = requiredString(value.basis, `${field}.basis`) as ContextMeshSupportingBasis;
        if (!(["purpose_context", "existing_capability", "explicit_standard", "inferred_standard"] as const).includes(basis)) {
            throw new Error(`Invalid Context Mesh report: ${field}.basis`);
        }
        supportingContext.basis = basis;
    }
    if (value.matchedBy != null) {
        const matchedBy = requiredString(value.matchedBy, `${field}.matchedBy`) as ContextMeshSupportingMatchedBy;
        if (!(["content", "content_and_file_context", "content_and_ticket_context"] as const).includes(matchedBy)) {
            throw new Error(`Invalid Context Mesh report: ${field}.matchedBy`);
        }
        supportingContext.matchedBy = matchedBy;
    }
    if (value.inferenceReason != null) {
        supportingContext.inferenceReason = requiredString(value.inferenceReason, `${field}.inferenceReason`);
    }
    return supportingContext;
}

function parseItem(value: unknown, index: number): ContextMeshReportItem {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: items[${index}]`);
    const score = value.score === null ? null : requiredNumber(value.score, `items[${index}].score`);
    const childCount = requiredNumber(value.childCount, `items[${index}].childCount`);
    const status = requiredString(value.status, `items[${index}].status`) as ContextMeshReportItemStatus;
    const relevance = requiredString(value.relevance, `items[${index}].relevance`) as ContextMeshReportRelevance;
    if (score !== null && (score < 0 || score > 100)) throw new Error(`Invalid Context Mesh report: items[${index}].score`);
    if (!Number.isInteger(childCount) || childCount < 0) throw new Error(`Invalid Context Mesh report: items[${index}].childCount`);
    if (!["rated", "not_rated", "processing", "failed"].includes(status)) throw new Error(`Invalid Context Mesh report: items[${index}].status`);
    if (!["relevant", "low_relevance", "not_rated"].includes(relevance)) throw new Error(`Invalid Context Mesh report: items[${index}].relevance`);
    if (typeof value.hasChildren !== "boolean") throw new Error(`Invalid Context Mesh report: items[${index}].hasChildren`);
    if (!value.hasChildren && childCount !== 0) throw new Error(`Invalid Context Mesh report: items[${index}].childCount`);
    const recommendation = value.recommendation == null ? null : parseRecommendation(value.recommendation);
    if (value.recommendation != null && recommendation === null) throw new Error(`Invalid Context Mesh report: items[${index}].recommendation`);
    const item: ContextMeshReportItem = {
        id: requiredString(value.id, `items[${index}].id`),
        type: requiredString(value.type, `items[${index}].type`),
        parentId: value.parentId == null ? null : requiredString(value.parentId, `items[${index}].parentId`),
        name: requiredString(value.name, `items[${index}].name`),
        path: value.path == null ? null : requiredString(value.path, `items[${index}].path`),
        score,
        status,
        relevance,
        relevanceReason: typeof value.relevanceReason === "string" ? value.relevanceReason : "",
        currentState: requiredString(value.currentState, `items[${index}].currentState`),
        method: requiredString(value.method, `items[${index}].method`),
        recommendation,
        hasChildren: value.hasChildren,
        childCount
    };
    if (value.recommendationStatus != null) {
        const recommendationStatus = requiredString(
            value.recommendationStatus,
            `items[${index}].recommendationStatus`
        ) as ContextMeshRecommendationStatus;
        if (!["not_requested", "analyzing", "ready", "failed"].includes(recommendationStatus)) {
            throw new Error(`Invalid Context Mesh report: items[${index}].recommendationStatus`);
        }
        if (recommendationStatus === "ready" && recommendation === null) {
            throw new Error(`Invalid Context Mesh report: items[${index}].recommendation`);
        }
        if (recommendationStatus !== "ready" && recommendation !== null) {
            throw new Error(`Invalid Context Mesh report: items[${index}].recommendation`);
        }
        item.recommendationStatus = recommendationStatus;
    }
    if (value.evidenceCounts != null) {
        item.evidenceCounts = parseEvidenceCounts(value.evidenceCounts, `items[${index}].evidenceCounts`);
    }
    if (value.contextRole != null) {
        const contextRole = requiredString(value.contextRole, `items[${index}].contextRole`) as ContextMeshReportContextRole;
        if (!["existing_product", "requested_feature", "standard_capability", "both", "none"].includes(contextRole)) {
            throw new Error(`Invalid Context Mesh report: items[${index}].contextRole`);
        }
        item.contextRole = contextRole;
    }
    if (value.coverageSummary != null) {
        item.coverageSummary = parseCoverageSummary(value.coverageSummary, `items[${index}].coverageSummary`);
    }
    if (value.evidenceRole != null) {
        const evidenceRole = requiredString(value.evidenceRole, `items[${index}].evidenceRole`) as ContextMeshReportEvidenceRole;
        if (!["direct_evidence", "supporting_product_context", "not_direct_evidence", "duplicate", "not_analyzed"].includes(evidenceRole)) {
            throw new Error(`Invalid Context Mesh report: items[${index}].evidenceRole`);
        }
        item.evidenceRole = evidenceRole;
    }
    if (value.scoreType !== undefined) {
        if (value.scoreType === null) {
            item.scoreType = null;
        } else {
            const scoreType = requiredString(value.scoreType, `items[${index}].scoreType`) as ContextMeshReportScoreType;
            if (!["direct_evidence", "support_relevance"].includes(scoreType)) {
                throw new Error(`Invalid Context Mesh report: items[${index}].scoreType`);
            }
            item.scoreType = scoreType;
        }
    } else if (value.strengthType !== undefined) {
        // Historical supporting-product-context-v1 payloads used strengthType.
        // Normalize them to the current score nomenclature at the API boundary.
        if (value.strengthType === null) {
            item.scoreType = null;
        } else {
            const strengthType = requiredString(value.strengthType, `items[${index}].strengthType`);
            if (strengthType !== "evidence" && strengthType !== "support") {
                throw new Error(`Invalid Context Mesh report: items[${index}].strengthType`);
            }
            item.scoreType = strengthType === "evidence" ? "direct_evidence" : "support_relevance";
        }
    }
    if (value.supportingContext != null) {
        item.supportingContext = parseSupportingContext(value.supportingContext, `items[${index}].supportingContext`);
    }
    if (value.detailStatus != null) {
        const detailStatus = requiredString(value.detailStatus, `items[${index}].detailStatus`) as ContextMeshReportDetailStatus;
        if (!["sufficient", "needs_detail", "not_applicable", "not_analyzed"].includes(detailStatus)) {
            throw new Error(`Invalid Context Mesh report: items[${index}].detailStatus`);
        }
        item.detailStatus = detailStatus;
    }
    if (value.contributesToScore !== undefined) {
        if (typeof value.contributesToScore !== "boolean") {
            throw new Error(`Invalid Context Mesh report: items[${index}].contributesToScore`);
        }
        item.contributesToScore = value.contributesToScore;
    }
    if (value.classificationCounts != null) {
        item.classificationCounts = parseClassificationCounts(value.classificationCounts, `items[${index}].classificationCounts`);
    }
    if (value.quality !== undefined || value.alignment !== undefined) {
        if (value.quality == null || value.alignment == null) {
            throw new Error(`Invalid Context Mesh report: items[${index}].qualityAlignment`);
        }
        item.quality = parseDiscoverQuality(value.quality, `items[${index}].quality`);
        item.alignment = parseDiscoverAlignment(value.alignment, `items[${index}].alignment`);
        if (item.score !== item.quality.score) {
            throw new Error(`Invalid Context Mesh report: items[${index}].score`);
        }
    }
    return item;
}

export function parseContextMeshReport(payload: unknown): ContextMeshReportPage {
    if (!isRecord(payload)) throw new Error("Invalid Context Mesh report: root");
    const category = requiredString(payload.category, "category") as ContextMeshCategoryKey;
    if (!["discover", "external", "file", "code"].includes(category)) throw new Error("Invalid Context Mesh report: category");
    const categoryScore = requiredNumber(payload.categoryScore, "categoryScore");
    if (categoryScore < 0 || categoryScore > 100) throw new Error("Invalid Context Mesh report: categoryScore");
    if (!Array.isArray(payload.items)) throw new Error("Invalid Context Mesh report: items");
    const report: ContextMeshReportPage = {
        productId: payload.productId == null ? null : requiredNumber(payload.productId, "productId"),
        contextMeshId: payload.contextMeshId == null ? null : requiredString(payload.contextMeshId, "contextMeshId"),
        runId: requiredString(payload.runId, "runId"),
        scoringVersion: requiredString(payload.scoringVersion, "scoringVersion"),
        formulaVersion: requiredString(payload.formulaVersion, "formulaVersion"),
        category,
        categoryScore,
        method: requiredString(payload.method, "method"),
        sourceDiagnostic: parseSourceDiagnostic(payload.sourceDiagnostic, "sourceDiagnostic"),
        items: payload.items.map(parseItem),
        nextCursor: payload.nextCursor == null ? null : requiredString(payload.nextCursor, "nextCursor")
    };
    if (payload.classificationVersion != null) {
        const classificationVersion = requiredString(payload.classificationVersion, "classificationVersion");
        if (
            classificationVersion !== SUPPORTING_CONTEXT_CLASSIFICATION_VERSION
            && !HISTORICAL_SUPPORTING_CONTEXT_CLASSIFICATION_VERSIONS.has(classificationVersion)
        ) {
            throw new Error("Invalid Context Mesh report: classificationVersion");
        }
        report.classificationVersion = classificationVersion;
    }
    return report;
}

export async function fetchContextMeshReport(
    token: string,
    target: ContextMeshReportTarget | number,
    query: ContextMeshReportQuery,
    signal?: AbortSignal
): Promise<ContextMeshReportPage> {
    const params = new URLSearchParams({ category: query.category, limit: String(query.limit ?? 50) });
    if (query.parentType && query.parentId) {
        params.set("parentType", query.parentType);
        params.set("parentId", query.parentId);
    }
    if (query.cursor) params.set("cursor", query.cursor);
    let response: Response;
    try {
        const normalizedTarget: ContextMeshReportTarget = typeof target === "number"
            ? { productId: target }
            : target;
        const path = "productId" in normalizedTarget
            ? `/api/products/${normalizedTarget.productId}/context-analysis/report`
            : `/api/context-meshes/${normalizedTarget.contextMeshId}/context-analysis/report`;
        response = await fetchWithAuth(`${path}?${params.toString()}`, token, {
            headers: { Accept: "application/json" },
            signal
        });
    } catch (reason) {
        if (reason instanceof DOMException && reason.name === "AbortError") throw reason;
        throw new ContextMeshApiError(503, { code: "REPORT_UNAVAILABLE", message: "The score detail report is temporarily unavailable." });
    }
    let payload: unknown;
    try {
        payload = await response.json();
    } catch {
        throw new ContextMeshApiError(response.status || 503, { code: "INVALID_RESPONSE", message: "The score detail report returned an invalid response." });
    }
    if (!response.ok) {
        const error = isRecord(payload) && isRecord(payload.error) ? payload.error : {};
        throw new ContextMeshApiError(response.status, {
            code: typeof error.code === "string" ? error.code : "REPORT_UNAVAILABLE",
            message: typeof error.message === "string" ? error.message : "The score detail report is temporarily unavailable."
        });
    }
    try {
        const report = parseContextMeshReport(payload);
        const normalizedTarget: ContextMeshReportTarget = typeof target === "number"
            ? { productId: target }
            : target;
        const identityMatches = "productId" in normalizedTarget
            ? report.productId === normalizedTarget.productId && report.contextMeshId == null
            : report.contextMeshId === normalizedTarget.contextMeshId && report.productId === null;
        if (!identityMatches || report.category !== query.category) throw new Error("Mismatched report provenance");
        return report;
    } catch {
        throw new ContextMeshApiError(503, { code: "INVALID_RESPONSE", message: "The score detail report returned an invalid response." });
    }
}

export async function scheduleContextMeshReportRecommendations(
    token: string,
    target: ContextMeshReportTarget | number,
    query: ContextMeshReportQuery,
    retryFailed = false,
    signal?: AbortSignal
): Promise<void> {
    const params = new URLSearchParams({ category: query.category, limit: String(query.limit ?? 50) });
    if (query.parentType && query.parentId) {
        params.set("parentType", query.parentType);
        params.set("parentId", query.parentId);
    }
    if (query.cursor) params.set("cursor", query.cursor);
    const normalizedTarget: ContextMeshReportTarget = typeof target === "number"
        ? { productId: target }
        : target;
    const path = "productId" in normalizedTarget
        ? `/api/products/${normalizedTarget.productId}/context-analysis/report/recommendations`
        : `/api/context-meshes/${normalizedTarget.contextMeshId}/context-analysis/report/recommendations`;
    const response = await fetchWithAuth(`${path}?${params.toString()}`, token, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ retryFailed }),
        signal
    });
    if (!response.ok) {
        throw new ContextMeshApiError(response.status, {
            code: "RECOMMENDATIONS_UNAVAILABLE",
            message: "Recommendations are temporarily unavailable."
        });
    }
}
